import cx from 'classnames';
import { ForwardedRef, forwardRef, InputHTMLAttributes, MouseEventHandler, useImperativeHandle, useRef } from 'react';

import { IconType } from 'utils/types';

import { inputSizes, InputSizeType, inputVariants, InputVariantType } from './utils';
import { defaultHoverTransition } from '../../utils/utils';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  variant?: InputVariantType;
  inputSize?: InputSizeType;
  containerClassName?: string;
  iconClassName?: string;
  isRounded?: boolean;
  hasBorder?: boolean;
  hasError?: boolean;
  icon?: IconType;
  onIconClick?: () => void;
  secondaryIcon?: IconType;
  onSecondaryIconClick?: () => void;
  secondaryIconClassName?: string;
  onContainerClick?: () => void;
  iconPosition?: 'start' | 'end';
}

const Input = (
  {
    variant = 'regular',
    inputSize = 'md',
    containerClassName,
    isRounded = true,
    hasBorder = true,
    hasError,
    icon: Icon,
    onIconClick,
    secondaryIcon: SecondaryIcon,
    onSecondaryIconClick,
    secondaryIconClassName,
    onContainerClick,
    iconClassName,
    className,
    disabled,
    iconPosition = 'end',
    required = true,
    ...rest
  }: InputProps,
  ref?: ForwardedRef<HTMLInputElement>
) => {
  const internalRef = useRef<HTMLInputElement>(null);
  useImperativeHandle<HTMLInputElement | null, HTMLInputElement | null>(ref, () => internalRef.current);

  const { inputStyle, borderStyle, iconColor } = inputVariants[variant];
  const isInputEmpty = !internalRef.current?.value;
  const isInputActive = !isInputEmpty && !hasError && !disabled;

  const iconSideClass = iconPosition === 'start' ? 'left-3' : 'right-3';
  const hasLeftIcon = !!Icon;
  const hasRightIcon = !!SecondaryIcon;
  const inputPaddingClasses = cx({ '!pl-12': hasRightIcon, '!pr-12': hasLeftIcon });

  const handleSecondaryIconClick: MouseEventHandler<SVGElement> = (e) => {
    if (!onSecondaryIconClick) return;

    e.stopPropagation();
    onSecondaryIconClick();
  };

  return (
    <div
      className={cx(
        'relative flex w-full items-center overflow-hidden focus-within:ring-4',
        defaultHoverTransition,
        {
          '!border-alert-50 border': hasError,
          [borderStyle.disabledColor]: disabled,
          [borderStyle.activeColor]: isInputActive,
          [borderStyle.defaultColor]: !disabled && !hasError && !isInputActive,
          'rounded-2xl': isRounded,
          border: hasBorder,
        },
        borderStyle.main,
        containerClassName
      )}
      onClick={onContainerClick}
    >
      <input
        className={cx(
          'peer w-full border-none font-light placeholder-shown:truncate focus:outline-none focus:ring-transparent',
          { 'rounded-2xl': isRounded },
          inputStyle,
          inputSizes[inputSize],
          inputPaddingClasses,
          className
        )}
        ref={internalRef}
        disabled={disabled}
        required={required}
        {...rest}
      />
      {SecondaryIcon && (
        <SecondaryIcon
          className={cx(
            'absolute h-6 w-6 select-none left-3',
            { 'cursor-pointer': !!onIconClick },
            iconColor,
            secondaryIconClassName
          )}
          onClick={handleSecondaryIconClick}
        />
      )}
      {Icon && (
        <Icon
          className={cx('absolute h-6 w-6 select-none cursor-pointer', iconColor, iconSideClass, iconClassName)}
          onClick={onIconClick}
        />
      )}
    </div>
  );
};

export default forwardRef(Input);
